import React, { useEffect, useState } from 'react';
import { Loader2, UserPlus, Building2 } from 'lucide-react';
import { atlasApi, AtlasUser, Colaborador, Filial } from '../shared/atlasApi';
import AvatarColaborador from '../shared/AvatarColaborador';
import BadgeStatus from '../shared/BadgeStatus';
import ModalNovoColaborador from './ModalNovoColaborador';

interface ListaProps {
  user: AtlasUser;
  busca: string;
  podeEditar: boolean;
  onAbrir: (funccodi: number) => void;
}

const ListaColaboradores: React.FC<ListaProps> = ({ user, busca, podeEditar, onAbrir }) => {
  const [itens, setItens] = useState<Colaborador[]>([]);
  const [total, setTotal] = useState(0);
  const [filtroStatus, setFiltroStatus] = useState<'ativo' | 'demitido' | ''>('ativo');
  const [filtroFilial, setFiltroFilial] = useState('');
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalNovo, setModalNovo] = useState(false);
  const [tick, setTick] = useState(0);
  const perPage = 30;

  // Debounce da busca
  const [buscaDebounce, setBuscaDebounce] = useState(busca);
  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounce(busca), 300);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => { setPage(1); }, [buscaDebounce, filtroStatus, filtroFilial]);

  // Filiais disponíveis (contagem acompanha o filtro de status)
  useEffect(() => {
    let cancel = false;
    atlasApi.listarFiliais(user, { status: filtroStatus || undefined })
      .then(r => { if (!cancel) setFiliais(r.itens || []); })
      .catch(e => { if (!cancel) console.error('[Atlas] filiais', e); });
    return () => { cancel = true; };
  }, [user, filtroStatus, tick]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    atlasApi.listarColaboradores(user, {
      status: filtroStatus || undefined,
      filial: filtroFilial || undefined,
      busca: buscaDebounce || undefined,
      page, per_page: perPage,
    })
      .then(r => { if (!cancel) { setItens(r.itens || []); setTotal(r.total || 0); } })
      .catch(e => { if (!cancel) console.error('[Atlas] lista', e); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, filtroStatus, filtroFilial, buscaDebounce, page, tick]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const nomeFilial = (funcempe?: number | null) =>
    filiais.find(f => f.funcempe === (funcempe ?? null))?.nome
    ?? (funcempe == null ? '—' : `Filial ${String(funcempe).padStart(2, '0')}`);

  return (
    <div className="p-6 space-y-4">
      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['ativo', 'demitido', ''] as const).map(s => (
          <button
            key={s || 'todos'}
            onClick={() => setFiltroStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              filtroStatus === s
                ? 'bg-purple-100 text-purple-700'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-purple-300'
            }`}
          >
            {s === 'ativo' ? 'Ativos' : s === 'demitido' ? 'Demitidos' : 'Todos'}
          </button>
        ))}

        {/* Filial (funcempe do GIX) */}
        <div className="relative">
          <Building2 className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <select
            value={filtroFilial}
            onChange={e => setFiltroFilial(e.target.value)}
            className={`pl-8 pr-3 py-1.5 rounded-full text-xs font-medium border appearance-none cursor-pointer transition ${
              filtroFilial
                ? 'bg-purple-50 border-purple-200 text-purple-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300'
            }`}
          >
            <option value="">Todas as filiais</option>
            {filiais.filter(f => f.funcempe != null).map(f => (
              <option key={f.funcempe} value={f.funcempe as number}>
                {f.nome} ({f.total})
              </option>
            ))}
          </select>
        </div>

        <span className="ml-auto text-xs text-gray-400">
          {loading ? 'Carregando...' : `${total} colaborador${total === 1 ? '' : 'es'}`}
        </span>
        {podeEditar && (
          <button
            onClick={() => setModalNovo(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold transition"
          >
            <UserPlus className="w-4 h-4" /> Novo colaborador
          </button>
        )}
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading && itens.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Carregando colaboradores...
          </div>
        ) : itens.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            Nenhum colaborador encontrado.
            <p className="text-xs mt-2">
              Se a sincronização nunca rodou, peça a um administrador para disparar em Configurações.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Colaborador</th>
                <th className="px-4 py-2 text-left">Cargo / Centro de custo</th>
                <th className="px-4 py-2 text-left">Filial</th>
                <th className="px-4 py-2 text-left">Admissão</th>
                <th className="px-4 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {itens.map(c => (
                <tr
                  key={c._id}
                  onClick={() => onAbrir(c.funccodi)}
                  className="hover:bg-purple-50/40 cursor-pointer transition"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <AvatarColaborador
                        nome={c.dados_publicos?.nome}
                        fotoUrl={(c.dados_publicos?.tem_foto_gix || c.enriquecimento?.foto_url) ? atlasApi.fotoUrl(user, c.funccodi, c.data_sincronizacao) : undefined}
                        size={36}
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{c.dados_publicos?.nome || '—'}</div>
                        <div className="text-xs text-gray-400">Mat. {c.dados_publicos?.matricula || c.funccodi}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-700">{c.dados_publicos?.cargo_nome || <span className="text-gray-300">sem cargo</span>}</div>
                    <div className="text-xs text-gray-400">{c.dados_publicos?.centro_custo_nome || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {nomeFilial(c.dados_publicos?.funcempe)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {c.dados_publicos?.data_admissao
                      ? new Date(c.dados_publicos.data_admissao).toLocaleDateString('pt-BR')
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <BadgeStatus status={c.dados_publicos?.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            ← Anterior
          </button>
          <span className="text-gray-500">Página {page} de {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Próxima →
          </button>
        </div>
      )}

      {modalNovo && (
        <ModalNovoColaborador
          user={user}
          onClose={() => setModalNovo(false)}
          onCriado={fc => {
            setModalNovo(false);
            setTick(t => t + 1);
            onAbrir(fc);
          }}
        />
      )}
    </div>
  );
};

export default ListaColaboradores;
